<template>
    <div class="card">
        <div class="card-body">
            <form @submit="fetchMaisons">
                <div class="row">
                   <div class="col-12 col-md-5">
                       <label>From</label>
                       <input type="date" class="form-control" v-model="formStartDate" required :disabled="isFetching" />
                   </div>
                   <div class="col-12 col-md-5">
                       <label>Up to and including</label>
                       <input type="date" class="form-control" v-model="formEndDate" required :disabled="isFetching" />
                   </div>
                   <div class="col-12 col-md-2">
                       <label>&nbsp;</label>
                       <button class="btn btn-primary w-100" :disabled="isFetching">
                           Set period
                       </button>
                   </div>
                </div>
            </form>
        </div>
    </div>

    <div class="card">
        <div class="card-body">
            <div class="row">
                <div class="col-12 col-md-3 d-flex mb-4">
                    <div>
                        <input type="checkbox" class="mt-2 mr-3" id="select-all" v-model="hasAllSelected" :disabled="isFetching" @click="selectAll" />
                    </div>
                    <div>
                        <label for="select-all">
                            All champagne houses<br/>
                            <small :class="{
                                'text-success': totalCount > 0
                            }">
                                <strong>{{ totalCount }}</strong> {{ totalCount === 1 ? "visit" : "visits" }} from period: {{ period }}
                            </small>
                        </label>
                    </div>
                </div>
                <div class="col-12 col-md-3 d-flex mb-4" v-for="option in maisons" :key="option.id">
                    <div>
                        <input type="checkbox" v-model="selectableMaisons[option.id]" :value="option.id" :id="option.id" class="mt-2 mr-3" :disabled="isFetching" />
                    </div>
                    <div>
                        <label :for="option.id">
                            <span v-html="option.name" /><br/>
                            <small :class="{
                                'text-success': option.total_bookings > 0
                            }">
                                <strong>{{ option.total_bookings }}</strong> {{ option.total_bookings === 1 ? "visit" : "visits" }} from period: {{ period }}
                            </small>
                        </label>
                    </div>
                </div>
            </div>
        </div>

        <div class="card-footer">
            <button @click="isOpen = true" :disabled="isFetching || !selectedAtleastOneMaison" class="btn btn-primary">
                Build invoices
            </button>
        </div>
    </div>

    <div v-if="isOpen" class="modal d-block" role="dialog">
        <div class="modal-dialog modal-xl" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Build invoices</h5>
                    <button
                        type="button"
                        class="close"
                        data-dismiss="modal"
                        aria-label="Close"
                        @click="closeConfirmation"
                        :disabled="isBuilding"
                    >
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
                <div class="modal-body">
                    <p v-if="hasBeenBuild">The invoices have been build. Click <a href="/admin/invoices">here</a> to go the overview.</p>
                    <p v-else>After clicking the build invoices button the invoices need a few seconds to generate!</p>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" @click="closeConfirmation">
                        Cancel
                    </button>
                    <button
                        type="button"
                        class="btn btn-primary"
                        @click="build"
                        :disabled="isBuilding || hasBeenBuild"
                    >
                        Build invoices
                        <div class="spinner-border spinner-border-sm ml-2" role="status" v-if="isBuilding">
                            <span class="sr-only">Loading...</span>
                        </div>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import {ref, computed, onMounted, reactive, watch} from "vue";
import axios from "axios";
import dayjs from "dayjs";

// If the building confirmation open.
const isOpen = ref(false);
// If the app is fetching maisons.
const isFetching = ref(false);
// If the app is building the invoices.
const isBuilding = ref(false);
// After the invoices have been build.
const hasBeenBuild = ref(false);
// Has all selected.
const hasAllSelected = ref(false);

// Property that'll hold all the fetched maisons.
const maisons = ref([]);
// The form object where the maison ids are the key and a boolean value to check them all is used.
const selectableMaisons = reactive({});

const selectedAtleastOneMaison = computed(() => {
    for (let maison in selectableMaisons) {
        if (selectableMaisons[maison]) {
            return true;
        }
    }

    return false;
})

// The dates that are used for the form.
const formStartDate = ref(dayjs().set('date', 1).subtract(1, 'month').format("YYYY-MM-DD"));
const formEndDate = ref(dayjs().set('date', 1).subtract(1, 'month').endOf('month').format("YYYY-MM-DD"));

// The dates that are used after the form is submitted to show for which period the fetched data is.
const startDate = ref(formStartDate.value);
const endDate = ref(formEndDate.value);

const period = computed(() => {
   const start = dayjs(startDate.value);
   const end = dayjs(endDate.value);

   if (start.format("MM YYYY") === end.format("MM YYYY")) {
       return start.format("MMMM");
   }

   return start.format("MMMM") + " - " + end.format("MMMM");
});

const fetchMaisons = async (e = null) => {
    isFetching.value = true;

    if (e !== null) {
        e.preventDefault();
    }

    const query = new URLSearchParams();
    query.append("start_date", formStartDate.value);
    query.append("end_date", endDate.value);

    const { data: { data } } = await axios.get('/admin/invoice-builder/maisons?' + query.toString());

    maisons.value = data;

    for (let maison of maisons.value) {
        selectableMaisons[maison.id] = false;
    }

    startDate.value = formStartDate.value;
    endDate.value = formEndDate.value;

    isFetching.value = false;
}

const selectAll = () => {
    for (let maison in selectableMaisons) {
        selectableMaisons[maison] = !hasAllSelected.value;
    }
}

const build = async () => {
    isBuilding.value = true;

    const query = new URLSearchParams();
    query.append("start_date", formStartDate.value);
    query.append("end_date", endDate.value);

    let selectedIds = [];
    for (let id in selectableMaisons) {
        if (!selectableMaisons[id]) continue;

        selectedIds.push(id)
    }
    query.append('ids', selectedIds.join(','))

    await axios.post('/admin/invoice-builder?' + query.toString());

    isBuilding.value = false;
    hasBeenBuild.value = true;
}

const closeConfirmation = () => {
    isOpen.value = false;
    hasBeenBuild.value = false;
}

onMounted(async () => {
    await fetchMaisons();
})

const totalCount = computed(() => maisons.value.reduce((value, maison) => value + maison.total_bookings, 0));

watch(selectableMaisons, (selectableMaisons) => {
    for (let maison in selectableMaisons) {
         if (!selectableMaisons[maison]) {
             hasAllSelected.value = false;
             return;
         }
    }

    hasAllSelected.value = true;
}, {
    deep: true
})
</script>
